import { useEffect, useRef, useState } from "react";

/**
 * Gives a conditionally-rendered element a real CSS exit transition instead
 * of an instant unmount. React removes a `{open && <div/>}` node from the
 * DOM the instant `open` flips false, which makes any `transition-*`
 * className on it useless for closing - there's no frame where both the
 * "closing" class and the node coexist. This hook keeps the node mounted
 * for `durationMs` after `open` goes false (`shouldRender`), and exposes a
 * separate `isVisible` flag that flips a tick after `shouldRender` becomes
 * true - so a caller can toggle its entrance/exit classes on `isVisible`
 * while gating whether to render at all on `shouldRender`.
 *
 * Usage:
 *   const { shouldRender, isVisible } = useMountTransition(open, 200);
 *   if (!shouldRender) return null;
 *   return <div className={cn("transition-all duration-200", isVisible ? "opacity-100" : "opacity-0")} />;
 */
export function useMountTransition(open: boolean, durationMs = 200): { shouldRender: boolean; isVisible: boolean } {
  const [shouldRender, setShouldRender] = useState(open);
  const [isVisible, setIsVisible] = useState(open);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    clearTimeout(timeoutRef.current);
    if (open) {
      setShouldRender(true);
      // A frame after mounting, so the browser has painted the "closed"
      // starting state before the transition to "open" begins - without
      // this the entrance would jump straight to its end state.
      const raf = requestAnimationFrame(() => setIsVisible(true));
      return () => cancelAnimationFrame(raf);
    }
    setIsVisible(false);
    timeoutRef.current = setTimeout(() => setShouldRender(false), durationMs);
    return () => clearTimeout(timeoutRef.current);
  }, [open, durationMs]);

  return { shouldRender, isVisible };
}
